import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { z } from "zod";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { FormsListQuerySchema } from "@/modules/forms/domain/contracts/forms";
import { FormResponsesPageQuerySchema } from "@/modules/forms/domain/contracts/public-forms";
import { SellPackagePage } from "@/modules/shared/presentation/pages/SellPackagePage";
import { PartnerPendingLockPage } from "@/modules/shared/presentation/pages/PartnerPendingLockPage";
import { PartnerProjectLockPricingPage } from "@/modules/shared/presentation/pages/PartnerProjectLockPricingPage";
import { SellPackageSuccessPage } from "@/modules/shared/presentation/pages/SellPackageSuccessPage";
import { LazyPage } from "@/shared/ui/LazyPage";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const DashboardPage = lazy(() =>
  import("@/modules/dashboard/presentation/pages/DashboardPage").then(
    (module) => ({
      default: module.DashboardPage,
    }),
  ),
);
const RolesPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);
const RoleCreatePage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleCreatePage").then(
    (module) => ({
      default: module.RoleCreatePage,
    }),
  ),
);
const RoleEditPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleEditPage").then((module) => ({
    default: module.RoleEditPage,
  })),
);
const UsersPage = lazy(() =>
  import("@/modules/users/presentation/pages/UsersPage").then((module) => ({
    default: module.UsersPage,
  })),
);
const UserCreatePage = lazy(() =>
  import("@/modules/users/presentation/pages/UserCreatePage").then(
    (module) => ({
      default: module.UserCreatePage,
    }),
  ),
);
const UserEditPage = lazy(() =>
  import("@/modules/users/presentation/pages/UserEditPage").then((module) => ({
    default: module.UserEditPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const FormsListPage = lazy(() =>
  import("@/modules/forms/presentation/pages/FormsListPage").then((m) => ({
    default: m.FormsListPage,
  })),
);
const FormsDashboardPage = lazy(() =>
  import("@/modules/forms/presentation/pages/FormsDashboardPage").then((m) => ({
    default: m.FormsDashboardPage,
  })),
);
const FormNewPage = lazy(() =>
  import("@/modules/forms/presentation/pages/FormNewPage").then((m) => ({
    default: m.FormNewPage,
  })),
);
const FormEditPage = lazy(() =>
  import("@/modules/forms/presentation/pages/FormEditPage").then((m) => ({
    default: m.FormEditPage,
  })),
);
const FormResponsesPage = lazy(() =>
  import("@/modules/forms/presentation/pages/FormResponsesPage").then((m) => ({
    default: m.FormResponsesPage,
  })),
);
const FormCategoriesPage = lazy(() =>
  import("@/modules/forms/presentation/pages/FormCategoriesPage").then((m) => ({
    default: m.FormCategoriesPage,
  })),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);

// Master Data Pages
const ClassTypesPage = lazy(() =>
  import("@/modules/master/presentation/pages/ClassTypesPage").then(
    (module) => ({
      default: module.ClassTypesPage,
    }),
  ),
);
const ClassTypeCreatePage = lazy(() =>
  import("@/modules/master/presentation/pages/ClassTypeCreatePage").then(
    (module) => ({
      default: module.ClassTypeCreatePage,
    }),
  ),
);
const ClassTypeEditPage = lazy(() =>
  import("@/modules/master/presentation/pages/ClassTypeEditPage").then(
    (module) => ({
      default: module.ClassTypeEditPage,
    }),
  ),
);
const LevelsPage = lazy(() =>
  import("@/modules/master/presentation/pages/LevelsPage").then((module) => ({
    default: module.LevelsPage,
  })),
);
const LevelCreatePage = lazy(() =>
  import("@/modules/master/presentation/pages/LevelCreatePage").then(
    (module) => ({
      default: module.LevelCreatePage,
    }),
  ),
);
const LevelEditPage = lazy(() =>
  import("@/modules/master/presentation/pages/LevelEditPage").then(
    (module) => ({
      default: module.LevelEditPage,
    }),
  ),
);
const RoomsPage = lazy(() =>
  import("@/modules/master/presentation/pages/RoomsPage").then((module) => ({
    default: module.RoomsPage,
  })),
);
const RoomCreatePage = lazy(() =>
  import("@/modules/master/presentation/pages/RoomCreatePage").then(
    (module) => ({
      default: module.RoomCreatePage,
    }),
  ),
);
const RoomEditPage = lazy(() =>
  import("@/modules/master/presentation/pages/RoomEditPage").then((module) => ({
    default: module.RoomEditPage,
  })),
);
const InstructorsPage = lazy(() =>
  import("@/modules/master/presentation/pages/InstructorsPage").then(
    (module) => ({
      default: module.InstructorsPage,
    }),
  ),
);
const InstructorCreatePage = lazy(() =>
  import("@/modules/master/presentation/pages/InstructorCreatePage").then(
    (module) => ({
      default: module.InstructorCreatePage,
    }),
  ),
);
const InstructorEditPage = lazy(() =>
  import("@/modules/master/presentation/pages/InstructorEditPage").then(
    (module) => ({
      default: module.InstructorEditPage,
    }),
  ),
);
const InstructorProfilePage = lazy(() =>
  import("@/modules/master/presentation/pages/InstructorProfilePage").then(
    (module) => ({
      default: module.default,
    }),
  ),
);
const StudentGroupsPage = lazy(() =>
  import("@/modules/master/presentation/pages/StudentGroupsPage").then(
    (module) => ({
      default: module.StudentGroupsPage,
    }),
  ),
);
const StudentGroupCreatePage = lazy(() =>
  import("@/modules/master/presentation/pages/StudentGroupCreatePage").then(
    (module) => ({
      default: module.StudentGroupCreatePage,
    }),
  ),
);
const StudentGroupEditPage = lazy(() =>
  import("@/modules/master/presentation/pages/StudentGroupEditPage").then(
    (module) => ({
      default: module.StudentGroupEditPage,
    }),
  ),
);
const StudentsPage = lazy(() =>
  import("@/modules/students/presentation/pages/StudentsPage").then(
    (module) => ({
      default: module.StudentsPage,
    }),
  ),
);
const StudentCreatePage = lazy(() =>
  import("@/modules/students/presentation/pages/StudentCreatePage").then(
    (module) => ({
      default: module.StudentCreatePage,
    }),
  ),
);
const StudentEditPage = lazy(() =>
  import("@/modules/students/presentation/pages/StudentEditPage").then(
    (module) => ({
      default: module.StudentEditPage,
    }),
  ),
);
const StudentProfilePage = lazy(() =>
  import("@/modules/students/presentation/pages/StudentProfilePage").then(
    (module) => {
      const Component = module?.default ?? module?.StudentProfilePage;
      if (!Component) {
        throw new Error(
          "StudentProfilePage failed to load (module or export missing)",
        );
      }
      return { default: Component };
    },
  ),
);

// Package Template Pages
const PackageTemplatesPage = lazy(() =>
  import("@/modules/packages/presentation/pages/PackageTemplatesPage").then(
    (module) => ({
      default: module.PackageTemplatesPage,
    }),
  ),
);
const PackageTemplateCreatePage = lazy(() =>
  import(
    "@/modules/packages/presentation/pages/PackageTemplateCreatePage"
  ).then((module) => ({
    default: module.PackageTemplateCreatePage,
  })),
);
const PackageTemplateEditPage = lazy(() =>
  import("@/modules/packages/presentation/pages/PackageTemplateEditPage").then(
    (module) => ({
      default: module.PackageTemplateEditPage,
    }),
  ),
);
const PackageTemplateDetailPage = lazy(() =>
  import(
    "@/modules/packages/presentation/pages/PackageTemplateDetailPage"
  ).then((module) => ({
    default: module.PackageTemplateDetailPage,
  })),
);

// Private Lesson Template Pages
const PrivateLessonTemplatesPage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonTemplatesPage"
  ).then((module) => ({
    default: module.PrivateLessonTemplatesPage,
  })),
);
const PrivateLessonTemplateCreatePage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonTemplateCreatePage"
  ).then((module) => ({
    default: module.PrivateLessonTemplateCreatePage,
  })),
);
const PrivateLessonTemplateEditPage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonTemplateEditPage"
  ).then((module) => ({
    default: module.PrivateLessonTemplateEditPage,
  })),
);
const PrivateLessonTemplateDetailPage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonTemplateDetailPage"
  ).then((module) => ({
    default: module.PrivateLessonTemplateDetailPage,
  })),
);
const BulkSchedulePrivateLessonsPage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/BulkSchedulePrivateLessonsPage"
  ).then((module) => ({
    default: module.BulkSchedulePrivateLessonsPage,
  })),
);
const PrivateLessonPackagesPage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonPackagesPage"
  ).then((module) => ({
    default: module.PrivateLessonPackagesPage,
  })),
);
const PrivateLessonPackageDetailPage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonPackageDetailPage"
  ).then((module) => ({
    default: module.PrivateLessonPackageDetailPage,
  })),
);
const PrivateLessonBookingsPage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonBookingsPage"
  ).then((module) => ({
    default: module.PrivateLessonBookingsPage,
  })),
);

// Room Bookings Pages
const RoomBookingsPage = lazy(() =>
  import("@/modules/room-bookings/presentation/pages/RoomBookingsPage").then(
    (module) => ({
      default: module.RoomBookingsPage,
    }),
  ),
);
const RoomBookingCreatePage = lazy(() =>
  import(
    "@/modules/room-bookings/presentation/pages/RoomBookingCreatePage"
  ).then((module) => ({
    default: module.RoomBookingCreatePage,
  })),
);
const RoomBookingBulkCreatePage = lazy(() =>
  import(
    "@/modules/room-bookings/presentation/pages/RoomBookingBulkCreatePage"
  ).then((module) => ({
    default: module.RoomBookingBulkCreatePage,
  })),
);
const RoomBookingDetailPage = lazy(() =>
  import(
    "@/modules/room-bookings/presentation/pages/RoomBookingDetailPage"
  ).then((module) => ({
    default: module.RoomBookingDetailPage,
  })),
);
const RoomBookingEditPage = lazy(() =>
  import("@/modules/room-bookings/presentation/pages/RoomBookingEditPage").then(
    (module) => ({
      default: module.RoomBookingEditPage,
    }),
  ),
);
const RoomBookingSuccessPage = lazy(() =>
  import(
    "@/modules/room-bookings/presentation/pages/RoomBookingSuccessPage"
  ).then((module) => ({
    default: module.RoomBookingSuccessPage,
  })),
);

// Room Displays Pages
const RoomDisplaysPage = lazy(() =>
  import("@/modules/room-displays/presentation/pages/RoomDisplaysPage").then(
    (module) => ({
      default: module.RoomDisplaysPage,
    }),
  ),
);
const RoomDisplayCreatePage = lazy(() =>
  import(
    "@/modules/room-displays/presentation/pages/RoomDisplayCreatePage"
  ).then((module) => ({
    default: module.RoomDisplayCreatePage,
  })),
);
const RoomDisplayDetailPage = lazy(() =>
  import(
    "@/modules/room-displays/presentation/pages/RoomDisplayDetailPage"
  ).then((module) => ({
    default: module.RoomDisplayDetailPage,
  })),
);
const RoomDisplayPublicPage = lazy(() =>
  import("@/modules/room-displays/presentation/pages/RoomDisplayPage").then(
    (module) => ({
      default: module.RoomDisplayPage,
    }),
  ),
);

// Class Template Pages
const ClassTemplatesPage = lazy(() =>
  import("@/modules/classes/presentation/pages/ClassTemplatesPage").then(
    (module) => ({
      default: module.ClassTemplatesPage,
    }),
  ),
);
const ClassTemplateCreatePage = lazy(() =>
  import("@/modules/classes/presentation/pages/ClassTemplateCreatePage").then(
    (module) => ({
      default: module.ClassTemplateCreatePage,
    }),
  ),
);
const ClassTemplateDetailPage = lazy(() =>
  import("@/modules/classes/presentation/pages/ClassTemplateDetailPage").then(
    (module) => ({
      default: module.ClassTemplateDetailPage,
    }),
  ),
);
const ClassTemplateEditPage = lazy(() =>
  import("@/modules/classes/presentation/pages/ClassTemplateEditPage").then(
    (module) => ({
      default: module.ClassTemplateEditPage,
    }),
  ),
);

// Class Session Pages
const ClassSessionsPage = lazy(() =>
  import("@/modules/classes/presentation/pages/ClassSessionsPage").then(
    (module) => ({
      default: module.ClassSessionsPage,
    }),
  ),
);
const ClassSessionCreatePage = lazy(() =>
  import("@/modules/classes/presentation/pages/ClassSessionCreatePage").then(
    (module) => ({
      default: module.ClassSessionCreatePage,
    }),
  ),
);
const BulkCreateSessionsPage = lazy(() =>
  import("@/modules/classes/presentation/pages/BulkCreateSessionsPage").then(
    (module) => ({
      default: module.BulkCreateSessionsPage,
    }),
  ),
);
const ClassSessionDetailPage = lazy(() =>
  import("@/modules/classes/presentation/pages/ClassSessionDetailPage").then(
    (module) => ({
      default: module.ClassSessionDetailPage,
    }),
  ),
);
const ClassSessionEditPage = lazy(() =>
  import("@/modules/classes/presentation/pages/ClassSessionEditPage").then(
    (module) => ({
      default: module.ClassSessionEditPage,
    }),
  ),
);
const ScheduleCalendarPage = lazy(() =>
  import("@/modules/classes/presentation/pages/ScheduleCalendarPage").then(
    (module) => ({
      default: module.ScheduleCalendarPage,
    }),
  ),
);
const ScheduleChangeLogsPage = lazy(() =>
  import("@/modules/classes/presentation/pages/ScheduleChangeLogsPage").then(
    (module) => ({
      default: module.ScheduleChangeLogsPage,
    }),
  ),
);
const ScheduleChangeLogDetailPage = lazy(() =>
  import(
    "@/modules/classes/presentation/pages/ScheduleChangeLogDetailPage"
  ).then((module) => ({
    default: module.ScheduleChangeLogDetailPage,
  })),
);
const PrivateLessonBookingCreatePage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonBookingCreatePage"
  ).then((module) => ({
    default: module.PrivateLessonBookingCreatePage,
  })),
);
const PrivateLessonBookingBulkCreatePage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonBookingBulkCreatePage"
  ).then((module) => ({
    default: module.PrivateLessonBookingBulkCreatePage,
  })),
);
const PrivateLessonBookingEditPage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonBookingEditPage"
  ).then((module) => ({
    default: module.PrivateLessonBookingEditPage,
  })),
);
const PrivateLessonBookingDetailPage = lazy(() =>
  import(
    "@/modules/private-lessons/presentation/pages/PrivateLessonBookingDetailPage"
  ).then((module) => ({
    default: module.PrivateLessonBookingDetailPage,
  })),
);

// Attendance (Check-in Hub)
const CheckInHubPage = lazy(() =>
  import("@/modules/attendance/presentation/pages/CheckInHubPage").then(
    (module) => ({
      default: module.CheckInHubPage,
    }),
  ),
);
const AttendanceRecordsPage = lazy(() =>
  import("@/modules/attendance/presentation/pages/AttendanceRecordsPage").then(
    (module) => ({
      default: module.AttendanceRecordsPage,
    }),
  ),
);
const ReceiptsPage = lazy(() =>
  import("@/modules/billing/presentation/pages/ReceiptsPage").then(
    (module) => ({
      default: module.ReceiptsPage,
    }),
  ),
);
const ReceiptDetailPage = lazy(() =>
  import("@/modules/billing/presentation/pages/ReceiptDetailPage").then(
    (module) => ({
      default: module.ReceiptDetailPage,
    }),
  ),
);
const CreditInvoicesPage = lazy(() =>
  import("@/modules/billing/presentation/pages/CreditInvoicesPage").then(
    (module) => ({
      default: module.CreditInvoicesPage,
    }),
  ),
);
const CreditInvoiceDetailPage = lazy(() =>
  import("@/modules/billing/presentation/pages/CreditInvoiceDetailPage").then(
    (module) => ({
      default: module.CreditInvoiceDetailPage,
    }),
  ),
);
const RefundRequestsPage = lazy(() =>
  import("@/modules/refunds/presentation/pages/RefundRequestsPage").then(
    (module) => ({
      default: module.RefundRequestsPage,
    }),
  ),
);
const CampEventConfigPage = lazy(() =>
  import(
    "@/modules/camp-registrations/presentation/pages/CampEventConfigPage"
  ).then((module) => ({
    default: module.CampEventConfigPage,
  })),
);
const CampRegistrationsPage = lazy(() =>
  import(
    "@/modules/camp-registrations/presentation/pages/CampRegistrationsPage"
  ).then((module) => ({
    default: module.CampRegistrationsPage,
  })),
);
const NotificationsPage = lazy(() =>
  import("@/modules/notifications/presentation/pages/NotificationsPage").then(
    (module) => ({
      default: module.NotificationsPage,
    }),
  ),
);
const CampRegistrationDetailPage = lazy(() =>
  import(
    "@/modules/camp-registrations/presentation/pages/CampRegistrationDetailPage"
  ).then((module) => ({
    default: module.CampRegistrationDetailPage,
  })),
);
const ClassEnrollmentsPage = lazy(() =>
  import(
    "@/modules/class-enrollments/presentation/pages/ClassEnrollmentsPage"
  ).then((module) => ({
    default: module.ClassEnrollmentsPage,
  })),
);
const ClassEnrollmentDetailPage = lazy(() =>
  import(
    "@/modules/class-enrollments/presentation/pages/ClassEnrollmentDetailPage"
  ).then((module) => ({
    default: module.ClassEnrollmentDetailPage,
  })),
);
const CampWaitlistPage = lazy(() =>
  import(
    "@/modules/camp-registrations/presentation/pages/CampWaitlistPage"
  ).then((module) => ({
    default: module.CampWaitlistPage,
  })),
);
const JamEventConfigPage = lazy(() =>
  import(
    "@/modules/jam-registrations/presentation/pages/JamEventConfigPage"
  ).then((module) => ({
    default: module.JamEventConfigPage,
  })),
);
const JamRegistrationsPage = lazy(() =>
  import(
    "@/modules/jam-registrations/presentation/pages/JamRegistrationsPage"
  ).then((module) => ({
    default: module.JamRegistrationsPage,
  })),
);
const JamRegistrationDetailPage = lazy(() =>
  import(
    "@/modules/jam-registrations/presentation/pages/JamRegistrationDetailPage"
  ).then((module) => ({
    default: module.JamRegistrationDetailPage,
  })),
);
const WorkshopRegistrationsPage = lazy(() =>
  import(
    "@/modules/workshop-registrations/presentation/pages/WorkshopRegistrationsPage"
  ).then((module) => ({
    default: module.WorkshopRegistrationsPage,
  })),
);
const VisitorLotteryEventPage = lazy(() =>
  import(
    "@/modules/visitor-lottery/presentation/pages/VisitorLotteryEventPage"
  ).then((module) => ({
    default: module.VisitorLotteryEventPage,
  })),
);
const VisitorRegistrationsPage = lazy(() =>
  import(
    "@/modules/visitor-lottery/presentation/pages/VisitorRegistrationsPage"
  ).then((module) => ({
    default: module.VisitorRegistrationsPage,
  })),
);
const VisitorLotteryScannerPage = lazy(() =>
  import(
    "@/modules/visitor-lottery/presentation/pages/VisitorLotteryScannerPage"
  ).then((module) => ({
    default: module.VisitorLotteryScannerPage,
  })),
);
const VisitorLotteryReportPage = lazy(() =>
  import(
    "@/modules/visitor-lottery/presentation/pages/VisitorLotteryReportPage"
  ).then((module) => ({
    default: module.VisitorLotteryReportPage,
  })),
);
const AudienceVotePage = lazy(() =>
  import(
    "@/modules/audience-vote/presentation/pages/AudienceVotePage"
  ).then((module) => ({
    default: module.AudienceVotePage,
  })),
);
const RefundRequestDetailPage = lazy(() =>
  import("@/modules/refunds/presentation/pages/RefundRequestDetailPage").then(
    (module) => ({
      default: module.RefundRequestDetailPage,
    }),
  ),
);
const ExpenseRecordsPage = lazy(() =>
  import("@/modules/expenses/presentation/pages/ExpenseRecordsPage").then(
    (module) => ({
      default: module.ExpenseRecordsPage,
    }),
  ),
);
const ExpenseRecordFormPage = lazy(() =>
  import("@/modules/expenses/presentation/pages/ExpenseRecordFormPage").then(
    (module) => ({
      default: module.ExpenseRecordFormPage,
    }),
  ),
);
const ExpenseRecordDetailPage = lazy(() =>
  import("@/modules/expenses/presentation/pages/ExpenseRecordDetailPage").then(
    (module) => ({
      default: module.ExpenseRecordDetailPage,
    }),
  ),
);
const ExpenseCategoriesPage = lazy(() =>
  import("@/modules/expenses/presentation/pages/ExpenseCategoriesPage").then(
    (module) => ({
      default: module.ExpenseCategoriesPage,
    }),
  ),
);
const ExpensesOverviewPage = lazy(() =>
  import("@/modules/expenses/presentation/pages/ExpensesOverviewPage").then(
    (module) => ({
      default: module.ExpensesOverviewPage,
    }),
  ),
);
const AttendanceReportPage = lazy(() =>
  import("@/modules/reports/presentation/pages/AttendanceReportPage").then(
    (module) => ({
      default: module.AttendanceReportPage,
    }),
  ),
);
const RoomUsageReportPage = lazy(() =>
  import("@/modules/reports/presentation/pages/RoomUsageReportPage").then(
    (module) => ({
      default: module.RoomUsageReportPage,
    }),
  ),
);
const InstructorSessionReportPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/InstructorSessionReportPage"
  ).then((module) => ({
    default: module.InstructorSessionReportPage,
  })),
);
const InstructorMonthlyPayoutReportPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/InstructorMonthlyPayoutReportPage"
  ).then((module) => ({
    default: module.InstructorMonthlyPayoutReportPage,
  })),
);
const RevenueSalesReportPage = lazy(() =>
  import("@/modules/reports/presentation/pages/RevenueSalesReportPage").then(
    (module) => ({
      default: module.RevenueSalesReportPage,
    }),
  ),
);
const PackageSalesReportPage = lazy(() =>
  import("@/modules/reports/presentation/pages/PackageSalesReportPage").then(
    (module) => ({
      default: module.PackageSalesReportPage,
    }),
  ),
);
const ClassTypeSalesReportPage = lazy(() =>
  import("@/modules/reports/presentation/pages/ClassTypeSalesReportPage").then(
    (module) => ({
      default: module.ClassTypeSalesReportPage,
    }),
  ),
);
const RefundReportPage = lazy(() =>
  import("@/modules/reports/presentation/pages/RefundReportPage").then(
    (module) => ({
      default: module.RefundReportPage,
    }),
  ),
);
const ClassCapacityUtilizationReportPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/ClassCapacityUtilizationReportPage"
  ).then((module) => ({
    default: module.ClassCapacityUtilizationReportPage,
  })),
);
const StudentReportPage = lazy(() =>
  import("@/modules/reports/presentation/pages/StudentReportPage").then(
    (module) => ({
      default: module.StudentReportPage,
    }),
  ),
);
const ActiveStudentsByClassReportPage = lazy(() =>
  import(
    "@/modules/reports/presentation/pages/ActiveStudentsByClassReportPage"
  ).then((module) => ({
    default: module.ActiveStudentsByClassReportPage,
  })),
);
const PackageNotificationsPage = lazy(() =>
  import(
    "@/modules/package-notifications/presentation/pages/PackageNotificationsPage"
  ).then((module) => ({
    default: module.PackageNotificationsPage,
  })),
);
const PackageNotificationsSendPage = lazy(() =>
  import(
    "@/modules/package-notifications/presentation/pages/PackageNotificationsSendPage"
  ).then((module) => ({
    default: module.PackageNotificationsSendPage,
  })),
);
const PackageNotificationsLogsPage = lazy(() =>
  import(
    "@/modules/package-notifications/presentation/pages/PackageNotificationsLogsPage"
  ).then((module) => ({
    default: module.PackageNotificationsLogsPage,
  })),
);
const PackageNotificationsSettingsPage = lazy(() =>
  import(
    "@/modules/package-notifications/presentation/pages/settings/PackageNotificationsSettingsPage"
  ).then((module) => ({
    default: module.PackageNotificationsSettingsPage,
  })),
);

// Performance Show Management
const RequestsPage = lazy(() =>
  import("@/modules/performance/presentation/pages/RequestsPage").then(
    (module) => ({ default: module.RequestsPage }),
  ),
);
const RequestCreatePage = lazy(() =>
  import("@/modules/performance/presentation/pages/RequestCreatePage").then(
    (module) => ({ default: module.RequestCreatePage }),
  ),
);
const RequestDetailPage = lazy(() =>
  import("@/modules/performance/presentation/pages/RequestDetailPage").then(
    (module) => ({ default: module.RequestDetailPage }),
  ),
);
const RequestConvertPage = lazy(() =>
  import("../modules/performance/presentation/pages/RequestConvertPage").then(
    (module) => ({ default: module.RequestConvertPage }),
  ),
);
const RequestEditPage = lazy(() =>
  import("@/modules/performance/presentation/pages/RequestEditPage").then(
    (module) => ({ default: module.RequestEditPage }),
  ),
);
const PerformersPage = lazy(() =>
  import("@/modules/performance/presentation/pages/PerformersPage").then(
    (module) => ({ default: module.PerformersPage }),
  ),
);
const PerformerCreatePage = lazy(() =>
  import("@/modules/performance/presentation/pages/PerformerCreatePage").then(
    (module) => ({ default: module.PerformerCreatePage }),
  ),
);
const PerformerDetailPage = lazy(() =>
  import("@/modules/performance/presentation/pages/PerformerDetailPage").then(
    (module) => ({ default: module.PerformerDetailPage }),
  ),
);
const PerformerEditPage = lazy(() =>
  import("@/modules/performance/presentation/pages/PerformerEditPage").then(
    (module) => ({ default: module.PerformerEditPage }),
  ),
);
const StaffPage = lazy(() =>
  import("@/modules/performance/presentation/pages/StaffPage").then(
    (module) => ({ default: module.StaffPage }),
  ),
);
const StaffCreatePage = lazy(() =>
  import("@/modules/performance/presentation/pages/StaffCreatePage").then(
    (module) => ({ default: module.StaffCreatePage }),
  ),
);
const StaffEditPage = lazy(() =>
  import("@/modules/performance/presentation/pages/StaffEditPage").then(
    (module) => ({ default: module.StaffEditPage }),
  ),
);
const OutsourcesPage = lazy(() =>
  import("@/modules/performance/presentation/pages/OutsourcesPage").then(
    (module) => ({ default: module.OutsourcesPage }),
  ),
);
const OutsourceCreatePage = lazy(() =>
  import("@/modules/performance/presentation/pages/OutsourceCreatePage").then(
    (module) => ({ default: module.OutsourceCreatePage }),
  ),
);
const OutsourceEditPage = lazy(() =>
  import("@/modules/performance/presentation/pages/OutsourceEditPage").then(
    (module) => ({ default: module.OutsourceEditPage }),
  ),
);
const OutsourceCategoriesPage = lazy(() =>
  import("@/modules/performance/presentation/pages/OutsourceCategoriesPage").then(
    (module) => ({ default: module.OutsourceCategoriesPage }),
  ),
);
const PaymentTermsTemplatesPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PaymentTermsTemplatesPage"
  ).then((module) => ({ default: module.PaymentTermsTemplatesPage })),
);
const PerDiemPoliciesPage = lazy(() =>
  import("@/modules/performance/presentation/pages/PerDiemPoliciesPage").then(
    (module) => ({ default: module.PerDiemPoliciesPage }),
  ),
);
const RoleFeeConfigsPage = lazy(() =>
  import("@/modules/performance/presentation/pages/RoleFeeConfigsPage").then(
    (module) => ({ default: module.RoleFeeConfigsPage }),
  ),
);
const PerDiemPolicyCreatePage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PerDiemPolicyCreatePage"
  ).then((module) => ({ default: module.PerDiemPolicyCreatePage })),
);
const PerDiemPolicyEditPage = lazy(() =>
  import("@/modules/performance/presentation/pages/PerDiemPolicyEditPage").then(
    (module) => ({ default: module.PerDiemPolicyEditPage }),
  ),
);
const EventCategoriesPage = lazy(() =>
  import("@/modules/performance/presentation/pages/EventCategoriesPage").then(
    (module) => ({ default: module.EventCategoriesPage }),
  ),
);
const EventCategoryCreatePage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/EventCategoryCreatePage"
  ).then((module) => ({ default: module.EventCategoryCreatePage })),
);
const EventCategoryEditPage = lazy(() =>
  import("@/modules/performance/presentation/pages/EventCategoryEditPage").then(
    (module) => ({ default: module.EventCategoryEditPage }),
  ),
);
const PaymentTermsTemplateCreatePage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PaymentTermsTemplateCreatePage"
  ).then((module) => ({ default: module.PaymentTermsTemplateCreatePage })),
);
const PaymentTermsTemplateEditPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PaymentTermsTemplateEditPage"
  ).then((module) => ({ default: module.PaymentTermsTemplateEditPage })),
);

const BankAccountTemplatesPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/BankAccountTemplatesPage"
  ).then((module) => ({ default: module.BankAccountTemplatesPage })),
);
const BankAccountTemplateCreatePage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/BankAccountTemplateCreatePage"
  ).then((module) => ({ default: module.BankAccountTemplateCreatePage })),
);
const BankAccountTemplateEditPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/BankAccountTemplateEditPage"
  ).then((module) => ({ default: module.BankAccountTemplateEditPage })),
);

const InstallmentPlanTemplatesPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/InstallmentPlanTemplatesPage"
  ).then((module) => ({ default: module.InstallmentPlanTemplatesPage })),
);
const InstallmentPlanTemplateCreatePage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/InstallmentPlanTemplateCreatePage"
  ).then((module) => ({ default: module.InstallmentPlanTemplateCreatePage })),
);
const InstallmentPlanTemplateEditPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/InstallmentPlanTemplateEditPage"
  ).then((module) => ({ default: module.InstallmentPlanTemplateEditPage })),
);
const OutsourceApInstallmentPlanTemplatesPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/OutsourceApInstallmentPlanTemplatesPage"
  ).then((module) => ({
    default: module.OutsourceApInstallmentPlanTemplatesPage,
  })),
);
const OutsourceApInstallmentPlanTemplateCreatePage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/OutsourceApInstallmentPlanTemplateCreatePage"
  ).then((module) => ({
    default: module.OutsourceApInstallmentPlanTemplateCreatePage,
  })),
);
const OutsourceApInstallmentPlanTemplateEditPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/OutsourceApInstallmentPlanTemplateEditPage"
  ).then((module) => ({
    default: module.OutsourceApInstallmentPlanTemplateEditPage,
  })),
);
const TaxSettingsPage = lazy(() =>
  import("@/modules/performance/presentation/pages/TaxSettingsPage").then(
    (module) => ({ default: module.TaxSettingsPage }),
  ),
);
const EventsPage = lazy(() =>
  import("@/modules/performance/presentation/pages/EventsPage").then(
    (module) => ({ default: module.EventsPage }),
  ),
);
const EventCreatePage = lazy(() =>
  import("@/modules/performance/presentation/pages/EventCreatePage").then(
    (module) => ({ default: module.EventCreatePage }),
  ),
);
const EventDetailPage = lazy(() =>
  import("@/modules/performance/presentation/pages/EventDetailPage").then(
    (module) => ({ default: module.EventDetailPage }),
  ),
);
const EventEditPage = lazy(() =>
  import("@/modules/performance/presentation/pages/EventEditPage").then(
    (module) => ({ default: module.EventEditPage }),
  ),
);
const EventRehearsalPage = lazy(() =>
  import("@/modules/performance/presentation/pages/EventRehearsalPage").then(
    (module) => ({ default: module.EventRehearsalPage }),
  ),
);
const DocumentsPage = lazy(() =>
  import("@/modules/performance/presentation/pages/DocumentsPage").then(
    (module) => ({ default: module.DocumentsPage }),
  ),
);
const PayoutsPage = lazy(() =>
  import("@/modules/performance/presentation/pages/PayoutsPage").then(
    (module) => ({ default: module.PayoutsPage }),
  ),
);
const PayoutDetailPage = lazy(() =>
  import("@/modules/performance/presentation/pages/PayoutDetailPage").then(
    (module) => ({ default: module.PayoutDetailPage }),
  ),
);
const PerformanceReportsDashboardPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PerformanceReportsDashboardPage"
  ).then((module) => ({
    default: module.PerformanceReportsDashboardPage,
  })),
);
const PerformanceEventReportPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PerformanceEventReportPage"
  ).then((module) => ({
    default: module.PerformanceEventReportPage,
  })),
);
const PerformancePayoutReportPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PerformancePayoutReportPage"
  ).then((module) => ({
    default: module.PerformancePayoutReportPage,
  })),
);
const PerformanceRevenueReportPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PerformanceRevenueReportPage"
  ).then((module) => ({
    default: module.PerformanceRevenueReportPage,
  })),
);
const PerformanceRequestReportPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PerformanceRequestReportPage"
  ).then((module) => ({
    default: module.PerformanceRequestReportPage,
  })),
);
const PerformancePerformerReportPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PerformancePerformerReportPage"
  ).then((module) => ({
    default: module.PerformancePerformerReportPage,
  })),
);
const PerformanceOperationsDashboardPage = lazy(() =>
  import(
    "@/modules/performance/presentation/pages/PerformanceOperationsDashboardPage"
  ).then((module) => ({
    default: module.PerformanceOperationsDashboardPage,
  })),
);

// Public Booking (no auth)
const PublicLayout = lazy(() =>
  import("./layout/PublicLayout").then((module) => ({
    default: module.PublicLayout,
  })),
);
const PublicBookingPage = lazy(() =>
  import("@/modules/public-booking/presentation/pages/PublicBookingPage").then(
    (module) => ({
      default: module.PublicBookingPage,
    }),
  ),
);
const PublicStudentProfilePage = lazy(() =>
  import(
    "@/modules/public-booking/presentation/pages/PublicStudentProfilePage"
  ).then((module) => ({
    default: module.PublicStudentProfilePage,
  })),
);
const PublicFormPage = lazy(() =>
  import("@/modules/forms/presentation/public/PublicFormPage").then(
    (module) => ({ default: module.PublicFormPage }),
  ),
);
const PublicFormSuccessPage = lazy(() =>
  import("@/modules/forms/presentation/public/PublicFormSuccessPage").then(
    (module) => ({ default: module.PublicFormSuccessPage }),
  ),
);
const PublicFormViewerGatePage = lazy(() =>
  import("@/modules/forms/presentation/public/PublicFormViewerGatePage").then(
    (module) => ({ default: module.PublicFormViewerGatePage }),
  ),
);
const PublicFormViewerResponsesPage = lazy(() =>
  import(
    "@/modules/forms/presentation/public/PublicFormViewerResponsesPage"
  ).then((module) => ({ default: module.PublicFormViewerResponsesPage })),
);

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location }) => {
    if (
      location.pathname === "/" ||
      location.pathname === "/app" ||
      location.pathname === "/app/"
    ) {
      throw redirect({ to: "/app/dashboard", replace: true });
    }
  },
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AuthenticatedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  validateSearch: z.object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
  }),
  beforeLoad: ({ search }) => {
    if (search.year != null && search.month != null) return;
    const now = new Date();
    throw redirect({
      to: "/app/dashboard",
      search: {
        year: now.getFullYear(),
        month: now.getMonth() + 1,
      },
      replace: true,
    });
  },
  component: () => (
    <LazyPage>
      <DashboardPage />
    </LazyPage>
  ),
});

const formsListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms",
  validateSearch: FormsListQuerySchema,
  component: () => (
    <RequirePermissions all={["forms:read"]}>
      <LazyPage>
        <FormsListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const formsDashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/dashboard",
  component: () => (
    <RequirePermissions all={["forms:read"]}>
      <LazyPage>
        <FormsDashboardPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const formsNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/new",
  component: () => (
    <RequirePermissions all={["forms:read", "forms:create"]}>
      <LazyPage>
        <FormNewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const formsCategoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/categories",
  component: () => (
    <RequirePermissions all={["forms:read"]}>
      <LazyPage>
        <FormCategoriesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const formsEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/$id/edit",
  component: () => (
    <RequirePermissions all={["forms:read", "forms:update"]}>
      <LazyPage>
        <FormEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const formsResponsesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/forms/$id/responses",
  validateSearch: FormResponsesPageQuerySchema,
  component: () => (
    <RequirePermissions all={["forms:read"]}>
      <LazyPage>
        <FormResponsesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RolesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: () => (
    <RequirePermissions all={["users:read"]}>
      <LazyPage>
        <UsersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:create"]}>
      <LazyPage>
        <UserCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:update"]}>
      <LazyPage>
        <UserEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

// Public Booking Routes (no auth, fullscreen for studio/kiosk)
const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/public",
  component: PublicLayout,
});

const publicBookingRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/booking",
  component: () => (
    <LazyPage>
      <PublicBookingPage />
    </LazyPage>
  ),
});

const publicStudentProfileRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "student/$memberId",
  component: () => (
    <LazyPage>
      <PublicStudentProfilePage />
    </LazyPage>
  ),
});

const studioLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/studio",
  component: PublicLayout,
});

const studioIndexRoute = createRoute({
  getParentRoute: () => studioLayoutRoute,
  path: "/",
  component: () => (
    <LazyPage>
      <PublicBookingPage />
    </LazyPage>
  ),
});

/** Public Forms (respondent, no auth) — `/f/:formId` */
const publicFormShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/f",
  component: PublicLayout,
});
const publicFormFillRoute = createRoute({
  getParentRoute: () => publicFormShellRoute,
  path: "$formId",
  component: () => (
    <LazyPage>
      <PublicFormPage />
    </LazyPage>
  ),
});
const publicFormSuccessRoute = createRoute({
  getParentRoute: () => publicFormShellRoute,
  path: "$formId/success",
  component: () => (
    <LazyPage>
      <PublicFormSuccessPage />
    </LazyPage>
  ),
});
const publicFormViewerResponsesRoute = createRoute({
  getParentRoute: () => publicFormShellRoute,
  path: "$formId/view/responses",
  validateSearch: FormResponsesPageQuerySchema,
  component: () => (
    <LazyPage>
      <PublicFormViewerResponsesPage />
    </LazyPage>
  ),
});
const publicFormViewerGateRoute = createRoute({
  getParentRoute: () => publicFormShellRoute,
  path: "$formId/view",
  component: () => (
    <LazyPage>
      <PublicFormViewerGatePage />
    </LazyPage>
  ),
});

// Students Routes
const studentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/students",
  component: () => (
    <RequirePermissions any={["students:read", "packages:sell"]}>
      <LazyPage>
        <StudentsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const studentProfileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/students/$id",
  validateSearch: z.object({
    printMembership: z.enum(["1"]).optional(),
  }),
  component: () => (
    <RequirePermissions all={["students:read"]}>
      <LazyPage>
        <StudentProfilePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const studentCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/students/create",
  component: () => (
    <RequirePermissions all={["students:read"]} any={["students:create"]}>
      <LazyPage>
        <StudentCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const studentEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/students/$id/edit",
  component: () => (
    <RequirePermissions all={["students:read"]} any={["students:update"]}>
      <LazyPage>
        <StudentEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Master Data Routes
const classTypesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/class-types",
  component: () => (
    <RequirePermissions all={["master:read"]}>
      <LazyPage>
        <ClassTypesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classTypeCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/class-types/create",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:create"]}>
      <LazyPage>
        <ClassTypeCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classTypeEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/class-types/$id/edit",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:update"]}>
      <LazyPage>
        <ClassTypeEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const levelsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/levels",
  component: () => (
    <RequirePermissions all={["master:read"]}>
      <LazyPage>
        <LevelsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const levelCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/levels/create",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:create"]}>
      <LazyPage>
        <LevelCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const levelEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/levels/$id/edit",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:update"]}>
      <LazyPage>
        <LevelEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/rooms",
  component: () => (
    <RequirePermissions all={["master:read"]}>
      <LazyPage>
        <RoomsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/rooms/create",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:create"]}>
      <LazyPage>
        <RoomCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/rooms/$id/edit",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:update"]}>
      <LazyPage>
        <RoomEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const instructorsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/instructors",
  component: () => (
    <RequirePermissions all={["master:read"]}>
      <LazyPage>
        <InstructorsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const instructorCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/instructors/create",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:create"]}>
      <LazyPage>
        <InstructorCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const instructorProfileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/instructors/$id",
  validateSearch: z.object({
    tab: z
      .enum(["general", "class-sessions", "private-lessons", "check-ins"])
      .optional(),
  }),
  component: () => (
    <RequirePermissions all={["master:read"]}>
      <LazyPage>
        <InstructorProfilePage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const instructorEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/instructors/$id/edit",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:update"]}>
      <LazyPage>
        <InstructorEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const studentGroupsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/student-groups",
  component: () => (
    <RequirePermissions all={["master:read"]}>
      <LazyPage>
        <StudentGroupsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const studentGroupCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/student-groups/create",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:create"]}>
      <LazyPage>
        <StudentGroupCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const studentGroupEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/master/student-groups/$id/edit",
  component: () => (
    <RequirePermissions all={["master:read"]} any={["master:update"]}>
      <LazyPage>
        <StudentGroupEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Package Template Routes
const packageTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/packages/templates",
  component: () => (
    <RequirePermissions all={["packages:read"]}>
      <LazyPage>
        <PackageTemplatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const packageTemplateCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/packages/templates/create",
  component: () => (
    <RequirePermissions all={["packages:read"]} any={["packages:create"]}>
      <LazyPage>
        <PackageTemplateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const packageTemplateDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/packages/templates/$id",
  component: () => (
    <RequirePermissions all={["packages:read"]}>
      <LazyPage>
        <PackageTemplateDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const packageTemplateEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/packages/templates/$id/edit",
  component: () => (
    <RequirePermissions all={["packages:read"]} any={["packages:update"]}>
      <LazyPage>
        <PackageTemplateEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Shared Sell Route
const sellRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sell",
  validateSearch: z.object({
    studentId: z.string().optional(),
    templateId: z.string().optional(),
    campRegistrationId: z.string().optional(),
    selectedRuleIds: z.string().optional(),
    type: z.preprocess((val) => {
      if (
        val === "private-lesson-group" ||
        val === "private-lesson-shared" ||
        val === "partner-off-site" ||
        val === "package"
      ) {
        return val;
      }
      return undefined;
    }, z
      .enum([
        "package",
        "private-lesson-group",
        "private-lesson-shared",
        "partner-off-site",
      ])
      .optional()),
  }),
  component: () => (
    <RequirePermissions
      all={["packages:read", "students:read"]}
      any={["packages:sell", "private-lessons:sell"]}
    >
      <LazyPage>
        <SellPackagePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const partnerPendingLockRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sell/partner/pending-lock",
  component: () => (
    <RequirePermissions any={["packages:sell"]}>
      <LazyPage>
        <PartnerPendingLockPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const partnerLockPricingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sell/partner/$id/lock-pricing",
  component: () => (
    <RequirePermissions any={["packages:sell"]}>
      <LazyPage>
        <PartnerProjectLockPricingPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const sellSuccessRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sell/success",
  validateSearch: z.object({
    type: z
      .enum([
        "package",
        "private-lesson",
        "private-lesson-group",
        "private-lesson-shared",
        "partner-off-site",
      ])
      .optional(),
    packageId: z.string().optional(),
    studentId: z.string().optional(),
    templateId: z.string().optional(),
    startDate: z.string().optional(),
    expireDate: z.string().optional(),
    originalPrice: z.string().optional(),
    discountAmount: z.string().optional(),
    finalPrice: z.string().optional(),
    discountType: z.string().optional(),
    discountValue: z.string().optional(),
    receiptId: z.string().optional(),
    selectedClassNames: z.string().optional(),
    /** ຈຳນວນຊົ່ວໂມງທີ່ຂາຍ (private lesson) */
    privateLessonHours: z.string().optional(),
    privateLessonGroupSize: z.string().optional(),
    settlementMode: z.enum(["receipt", "credit"]).optional(),
    creditInvoiceId: z.string().optional(),
    creditPaid: z.string().optional(),
    creditRemaining: z.string().optional(),
    partnerSaleId: z.string().optional(),
    partnerLabel: z.string().optional(),
    partnerOffSiteDistance: z.enum(["near", "far"]).optional(),
    partnerInstallmentCount: z.string().optional(),
    partnerClassLabels: z.string().optional(),
    partnerInstructorLabels: z.string().optional(),
    partnerSoldAt: z.string().optional(),
    partnerSessionsScheduled: z.string().optional(),
    partnerPricingStatus: z.enum(["draft", "quoted", "locked"]).optional(),
    /** ຈຳນວນຄັ້ງຈອງທີ່ສ້າງຕອນຂາຍ (private lesson) */
    privateLessonSessionsScheduled: z.string().optional(),
    /** stacked class package */
    quantity: z.string().optional(),
    purchaseBatchId: z.string().optional(),
    /** periodIndex:start:expire;... */
    stackedPeriods: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions any={["packages:sell", "private-lessons:sell"]}>
      <LazyPage>
        <SellPackageSuccessPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/templates",
  component: () => (
    <RequirePermissions all={["private-lessons:read"]}>
      <LazyPage>
        <PrivateLessonTemplatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonTemplateCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/templates/create",
  component: () => (
    <RequirePermissions all={["private-lessons:create"]}>
      <LazyPage>
        <PrivateLessonTemplateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonTemplateDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/templates/$id",
  component: () => (
    <RequirePermissions all={["private-lessons:read"]}>
      <LazyPage>
        <PrivateLessonTemplateDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonTemplateEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/templates/$id/edit",
  component: () => (
    <RequirePermissions all={["private-lessons:update"]}>
      <LazyPage>
        <PrivateLessonTemplateEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonPackagesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/packages",
  component: () => (
    <RequirePermissions all={["private-lessons:read"]}>
      <LazyPage>
        <PrivateLessonPackagesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const privateLessonPackageDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/packages/$packageId",
  component: () => (
    <RequirePermissions all={["private-lessons:read"]}>
      <LazyPage>
        <PrivateLessonPackageDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const bulkSchedulePrivateLessonsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/packages/$packageId/schedule",
  component: () => (
    <RequirePermissions all={["private-lessons:create"]}>
      <LazyPage>
        <BulkSchedulePrivateLessonsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonBookingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/bookings",
  component: () => (
    <RequirePermissions all={["private-lessons:read"]}>
      <LazyPage>
        <PrivateLessonBookingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonBookingCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/bookings/create",
  validateSearch: z.object({
    studentId: z.string().optional(),
    packageId: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions all={["private-lessons:create"]}>
      <LazyPage>
        <PrivateLessonBookingCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonBookingBulkCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/bookings/bulk-create",
  validateSearch: z.object({
    studentId: z.string().optional(),
    packageId: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions all={["private-lessons:create"]}>
      <LazyPage>
        <PrivateLessonBookingBulkCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonBookingDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/bookings/$id",
  component: () => (
    <RequirePermissions all={["private-lessons:read"]}>
      <LazyPage>
        <PrivateLessonBookingDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const privateLessonBookingEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/private-lessons/bookings/$id/edit",
  component: () => (
    <RequirePermissions all={["private-lessons:update"]}>
      <LazyPage>
        <PrivateLessonBookingEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Room Bookings Routes

const roomDisplaysRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/room-displays",
  component: () => (
    <RequirePermissions all={["room-displays:read"]}>
      <LazyPage>
        <RoomDisplaysPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomDisplayCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/room-displays/new",
  component: () => (
    <RequirePermissions all={["room-displays:update"]}>
      <LazyPage>
        <RoomDisplayCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomDisplayDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/room-displays/$id",
  component: () => (
    <RequirePermissions all={["room-displays:read"]}>
      <LazyPage>
        <RoomDisplayDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const displayLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/display",
  component: PublicLayout,
});

const displaySlugRoute = createRoute({
  getParentRoute: () => displayLayoutRoute,
  path: "/$slug",
  component: () => (
    <LazyPage>
      <RoomDisplayPublicPage />
    </LazyPage>
  ),
});

const roomBookingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/room-bookings",
  component: () => (
    <RequirePermissions all={["room-bookings:read"]}>
      <LazyPage>
        <RoomBookingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomBookingCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/room-bookings/new",
  component: () => (
    <RequirePermissions all={["room-bookings:create"]}>
      <LazyPage>
        <RoomBookingCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomBookingBulkCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/room-bookings/bulk-create",
  validateSearch: z.object({
    date: z.string().optional(),
    startTime: z.string().optional(),
    endTime: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions all={["room-bookings:create"]}>
      <LazyPage>
        <RoomBookingBulkCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomBookingSuccessRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/room-bookings/success",
  validateSearch: z.object({
    bookingId: z.string(),
    settlementMode: z.enum(["receipt", "credit"]).optional(),
    receiptId: z.string().optional(),
    creditInvoiceId: z.string().optional(),
    creditPaid: z.string().optional(),
    creditRemaining: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions all={["room-bookings:read"]}>
      <LazyPage>
        <RoomBookingSuccessPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomBookingDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/room-bookings/$id",
  component: () => (
    <RequirePermissions all={["room-bookings:read"]}>
      <LazyPage>
        <RoomBookingDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomBookingEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/room-bookings/$id/edit",
  component: () => (
    <RequirePermissions all={["room-bookings:update"]}>
      <LazyPage>
        <RoomBookingEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Class Templates Routes
const classTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/templates",
  component: () => (
    <RequirePermissions all={["classes:read"]}>
      <LazyPage>
        <ClassTemplatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classTemplateCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/templates/create",
  component: () => (
    <RequirePermissions all={["classes:create"]}>
      <LazyPage>
        <ClassTemplateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classTemplateDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/templates/$id",
  component: () => (
    <RequirePermissions all={["classes:read"]}>
      <LazyPage>
        <ClassTemplateDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classTemplateEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/templates/$id/edit",
  component: () => (
    <RequirePermissions all={["classes:update"]}>
      <LazyPage>
        <ClassTemplateEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Class Schedule Calendar Route
const classScheduleRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/schedule",
  component: () => (
    <RequirePermissions all={["classes:read"]}>
      <LazyPage>
        <ScheduleCalendarPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Class Sessions Routes
const classSessionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/sessions",
  component: () => (
    <RequirePermissions all={["classes:read"]}>
      <LazyPage>
        <ClassSessionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classSessionCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/sessions/create",
  component: () => (
    <RequirePermissions all={["classes:create"]}>
      <LazyPage>
        <ClassSessionCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classSessionBulkCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/sessions/bulk-create",
  component: () => (
    <RequirePermissions all={["classes:create"]}>
      <LazyPage>
        <BulkCreateSessionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classSessionDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/sessions/$id",
  component: () => (
    <RequirePermissions all={["classes:read"]}>
      <LazyPage>
        <ClassSessionDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classSessionEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/sessions/$id/edit",
  component: () => (
    <RequirePermissions all={["classes:update"]}>
      <LazyPage>
        <ClassSessionEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Schedule Change Logs Routes
const scheduleChangeLogsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/schedule-logs",
  component: () => (
    <RequirePermissions all={["classes:read"]}>
      <LazyPage>
        <ScheduleChangeLogsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});
const scheduleChangeLogDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/classes/schedule-logs/$id",
  component: () => (
    <RequirePermissions all={["classes:read"]}>
      <LazyPage>
        <ScheduleChangeLogDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Attendance Check-in Hub
const attendanceCheckInRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/attendance/check-in",
  component: () => (
    <RequirePermissions any={["classes:read", "private-lessons:read"]}>
      <LazyPage>
        <CheckInHubPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Attendance Records (ບັນທຶກການເຂົ້າຮຽນ)
const attendanceRecordsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/attendance/records",
  component: () => (
    <RequirePermissions any={["classes:read", "private-lessons:read"]}>
      <LazyPage>
        <AttendanceRecordsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// Billing Routes
const receiptsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/receipts",
  component: () => (
    <RequirePermissions all={["billing:read"]}>
      <LazyPage>
        <ReceiptsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const receiptDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/receipts/$id",
  component: () => (
    <RequirePermissions all={["billing:read"]}>
      <LazyPage>
        <ReceiptDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const creditInvoicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/credit-invoices",
  validateSearch: z.object({
    limit: z.coerce.number().optional(),
    offset: z.coerce.number().optional(),
    outstanding: z.string().optional(),
    sort: z.union([z.string(), z.array(z.string())]).optional(),
    refType: z.string().optional(),
    name: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions all={["billing:read"]}>
      <LazyPage>
        <CreditInvoicesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const creditInvoiceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/billing/credit-invoices/$id",
  component: () => (
    <RequirePermissions all={["billing:read"]}>
      <LazyPage>
        <CreditInvoiceDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const refundsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/refunds",
  component: () => (
    <RequirePermissions all={["refunds:read"]}>
      <LazyPage>
        <RefundRequestsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const expensesOverviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses/overview",
  component: () => (
    <RequirePermissions all={["expenses:read"]}>
      <LazyPage>
        <ExpensesOverviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const expensesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses",
  component: () => (
    <RequirePermissions all={["expenses:read"]}>
      <LazyPage>
        <ExpenseRecordsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const expenseCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses/new",
  component: () => (
    <RequirePermissions all={["expenses:create"]}>
      <LazyPage>
        <ExpenseRecordFormPage mode="create" />
      </LazyPage>
    </RequirePermissions>
  ),
});

const expenseCategoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses/categories",
  component: () => (
    <RequirePermissions all={["expenses:manage-categories"]}>
      <LazyPage>
        <ExpenseCategoriesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const expenseEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses/$id/edit",
  component: () => (
    <RequirePermissions all={["expenses:update"]}>
      <LazyPage>
        <ExpenseRecordFormPage mode="edit" />
      </LazyPage>
    </RequirePermissions>
  ),
});

const expenseDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses/$id",
  component: () => (
    <RequirePermissions all={["expenses:read"]}>
      <LazyPage>
        <ExpenseRecordDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const campEventsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/camp-events",
  component: () => (
    <RequirePermissions all={["camp-events:read"]}>
      <LazyPage>
        <CampEventConfigPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const campRegistrationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/camp-registrations",
  component: () => (
    <RequirePermissions all={["camp-registrations:read"]}>
      <LazyPage>
        <CampRegistrationsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const campWaitlistRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/camp-registrations/waitlist",
  validateSearch: (search: Record<string, unknown>) => ({
    classTypeId:
      typeof search.classTypeId === "string" ? search.classTypeId : undefined,
  }),
  component: () => (
    <RequirePermissions all={["camp-registrations:read"]}>
      <LazyPage>
        <CampWaitlistPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const campRegistrationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/camp-registrations/$id",
  component: () => (
    <RequirePermissions all={["camp-registrations:read"]}>
      <LazyPage>
        <CampRegistrationDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classEnrollmentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/class-enrollments",
  validateSearch: (search: Record<string, unknown>) => ({
    limit: Number(search.limit ?? 20) || 20,
    offset: Number(search.offset ?? 0) || 0,
    status: typeof search.status === "string" ? search.status : undefined,
    phone: typeof search.phone === "string" ? search.phone : undefined,
  }),
  component: () => (
    <RequirePermissions all={["class-enrollments:read"]}>
      <LazyPage>
        <ClassEnrollmentsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classEnrollmentDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/class-enrollments/$id",
  component: () => (
    <RequirePermissions all={["class-enrollments:read"]}>
      <LazyPage>
        <ClassEnrollmentDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const jamEventsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/jam-events",
  component: () => (
    <RequirePermissions all={["jam-events:read"]}>
      <LazyPage>
        <JamEventConfigPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const jamRegistrationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/jam-registrations",
  component: () => (
    <RequirePermissions all={["jam-registrations:read"]}>
      <LazyPage>
        <JamRegistrationsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const jamRegistrationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/jam-registrations/$id",
  component: () => (
    <RequirePermissions all={["jam-registrations:read"]}>
      <LazyPage>
        <JamRegistrationDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const workshopRegistrationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/workshop-registrations",
  component: () => (
    <RequirePermissions all={["workshop-registrations:read"]}>
      <LazyPage>
        <WorkshopRegistrationsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const visitorLotteryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/visitor-lottery",
  component: () => (
    <RequirePermissions all={["visitor-lottery:read"]}>
      <LazyPage>
        <VisitorLotteryEventPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const visitorLotteryRegistrationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/visitor-lottery/registrations",
  validateSearch: (search: Record<string, unknown>) => ({
    limit: typeof search.limit === "number" ? search.limit : undefined,
    offset: typeof search.offset === "number" ? search.offset : undefined,
    eventId: typeof search.eventId === "string" ? search.eventId : undefined,
    prizeId: typeof search.prizeId === "string" ? search.prizeId : undefined,
    redeemed: typeof search.redeemed === "string" ? search.redeemed : undefined,
    won: typeof search.won === "string" ? search.won : undefined,
    search: typeof search.search === "string" ? search.search : undefined,
  }),
  component: () => (
    <RequirePermissions all={["visitor-lottery:read"]}>
      <LazyPage>
        <VisitorRegistrationsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const visitorLotteryScannerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/visitor-lottery/scanner",
  component: () => (
    <RequirePermissions all={["visitor-lottery:write"]}>
      <LazyPage>
        <VisitorLotteryScannerPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const visitorLotteryReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/visitor-lottery/report",
  component: () => (
    <RequirePermissions all={["visitor-lottery:read"]}>
      <LazyPage>
        <VisitorLotteryReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const audienceVoteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audience-vote",
  component: () => (
    <RequirePermissions all={["audience-vote:read"]}>
      <LazyPage>
        <AudienceVotePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notifications",
  component: () => (
    <RequirePermissions all={["camp-registrations:read"]}>
      <LazyPage>
        <NotificationsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const refundDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/refunds/$id",
  component: () => (
    <RequirePermissions all={["refunds:read"]}>
      <LazyPage>
        <RefundRequestDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const attendanceReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/attendance",
  validateSearch: z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    classTypeId: z.string().optional(),
    levelId: z.string().optional(),
    instructorId: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <AttendanceReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roomUsageReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/room-usage",
  validateSearch: z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    roomId: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <RoomUsageReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const instructorSessionReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/instructor-session",
  validateSearch: z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    instructorId: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <InstructorSessionReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const instructorMonthlyPayoutReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/instructor-monthly-payout",
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <InstructorMonthlyPayoutReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const revenueSalesReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/revenue",
  validateSearch: z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    issuedById: z.string().optional(),
    groupBy: z.enum(["day", "month"]).optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <RevenueSalesReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const packageSalesReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/package-sales",
  validateSearch: z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    packageType: z.enum(["package", "private_lesson", "all"]).optional(),
    classTypeId: z.string().optional(),
    levelId: z.string().optional(),
    sortBy: z.enum(["count", "revenue"]).optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <PackageSalesReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classTypeSalesReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/class-type-sales",
  validateSearch: z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    levelId: z.string().optional(),
    sortBy: z.enum(["count", "revenue"]).optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <ClassTypeSalesReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const refundReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/refund",
  validateSearch: z.object({
    filters: z.any().optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <RefundReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const classCapacityReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/class-capacity",
  validateSearch: z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    classTypeId: z.string().optional(),
    levelId: z.string().optional(),
    roomId: z.string().optional(),
    locationKind: z.enum(["studio", "off_site"]).optional(),
    engagementKind: z.enum(["standard", "partner_instructor_only"]).optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <ClassCapacityUtilizationReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const studentReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/students",
  validateSearch: z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    groupId: z.string().optional(),
    packageType: z.enum(["package", "private_lesson", "all"]).optional(),
    groupBy: z.enum(["day", "month"]).optional(),
    thresholdSessions: z.coerce.number().optional(),
    thresholdHours: z.coerce.number().optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <StudentReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const activeStudentsByClassReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/active-students-by-class",
  validateSearch: z.object({
    classTypeId: z.string().optional(),
    levelId: z.string().optional(),
    groupId: z.string().optional(),
    sortBy: z.enum(["count", "className"]).optional(),
    includeEmpty: z.enum(["true", "false"]).optional(),
  }),
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <ActiveStudentsByClassReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const packageNotificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/package-notifications",
  component: () => (
    <RequirePermissions all={["package-notifications:read"]}>
      <LazyPage>
        <PackageNotificationsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const packageNotificationsSendRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/package-notifications/send",
  component: () => (
    <RequirePermissions all={["package-notifications:send"]}>
      <LazyPage>
        <PackageNotificationsSendPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const packageNotificationsLogsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/package-notifications/logs",
  component: () => (
    <RequirePermissions all={["package-notifications:read"]}>
      <LazyPage>
        <PackageNotificationsLogsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const packageNotificationsSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/package-notifications",
  component: () => (
    <RequirePermissions all={["package-notifications:manage"]}>
      <LazyPage>
        <PackageNotificationsSettingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

// ─── Performance Show Management Routes ───────────────────────────────────────

function parsePerformanceSearchSort(value: unknown) {
  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return undefined;
    }
  }
  if (!Array.isArray(parsed)) return undefined;

  const normalized = parsed
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const rawField = (item as { field?: unknown }).field;
      const dir = (item as { dir?: unknown }).dir;
      let field: string | undefined;

      if (typeof rawField === "string") {
        field = rawField;
      } else if (Array.isArray(rawField)) {
        const first = rawField[0];
        if (typeof first === "object" && first !== null && "id" in first) {
          field = String((first as { id: unknown }).id ?? "");
        } else {
          field = typeof first === "string" ? first : String(first ?? "");
        }
      }

      if (!field || (dir !== "asc" && dir !== "desc")) return null;
      return { field, dir };
    })
    .filter((item): item is { field: string; dir: "asc" | "desc" } => !!item);

  return normalized.length > 0 ? normalized : undefined;
}

const performanceSearchSchema = z.object({
  offset: z.coerce.number().int().min(0).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  sort: z.preprocess(
    parsePerformanceSearchSort,
    z
      .array(z.object({ field: z.string(), dir: z.enum(["asc", "desc"]) }))
      .optional(),
  ),
  filters: z.any().optional(),
});

const eventsPageSearchSchema = performanceSearchSchema.extend({
  view: z.enum(["list", "calendar"]).optional(),
  calendarLayer: z.enum(["events", "shows"]).optional(),
  calendarMonth: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
});

const requestsPageSearchSchema = performanceSearchSchema.extend({
  view: z.enum(["list", "calendar"]).optional(),
  calendarMonth: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
});

const staffSearchSchema = performanceSearchSchema.extend({
  q: z.string().optional(),
});

const outsourcesSearchSchema = staffSearchSchema.extend({
  categoryId: z.string().optional(),
  payeeType: z.enum(["individual", "team"]).optional(),
  status: z.enum(["active", "inactive"]).optional(),
});

const performanceReportSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  groupBy: z.enum(["day", "week", "month"]).optional(),
  status: z.string().optional(),
  documentType: z.string().optional(),
  view: z.enum(["overview", "profitability"]).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

const performanceOperationsDashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/dashboard",
  component: () => (
    <RequirePermissions
      any={[
        "performance-requests:read",
        "performance-events:read",
        "performance-documents:read",
        "performance-payouts:read",
        "performance-reports:read",
      ]}
    >
      <LazyPage>
        <PerformanceOperationsDashboardPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performanceIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance",
  beforeLoad: () => {
    throw redirect({ to: "/app/performance/dashboard", replace: true });
  },
});

const requestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/requests",
  validateSearch: requestsPageSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-requests:read"]}>
      <LazyPage>
        <RequestsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const requestCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/requests/create",
  component: () => (
    <RequirePermissions
      all={["performance-requests:read"]}
      any={["performance-requests:create"]}
    >
      <LazyPage>
        <RequestCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const requestDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/requests/$id",
  component: () => (
    <RequirePermissions all={["performance-requests:read"]}>
      <LazyPage>
        <RequestDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const requestConvertRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/requests/$id/convert",
  component: () => (
    <RequirePermissions
      all={[
        "performance-requests:read",
        "performance-requests:update",
        "performance-events:create",
      ]}
    >
      <LazyPage>
        <RequestConvertPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const requestEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/requests/$id/edit",
  component: () => (
    <RequirePermissions
      all={["performance-requests:read"]}
      any={["performance-requests:update"]}
    >
      <LazyPage>
        <RequestEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/performers",
  validateSearch: performanceSearchSchema,
  component: () => (
    <RequirePermissions all={["performers:read"]}>
      <LazyPage>
        <PerformersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performerCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/performers/create",
  component: () => (
    <RequirePermissions all={["performers:read"]} any={["performers:create"]}>
      <LazyPage>
        <PerformerCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/performers/$id",
  component: () => (
    <RequirePermissions all={["performers:read"]}>
      <LazyPage>
        <PerformerDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performerEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/performers/$id/edit",
  component: () => (
    <RequirePermissions all={["performers:read"]} any={["performers:update"]}>
      <LazyPage>
        <PerformerEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const staffRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/staff",
  validateSearch: staffSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-payouts:read"]}>
      <LazyPage>
        <StaffPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const staffCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/staff/create",
  component: () => (
    <RequirePermissions
      all={["performance-payouts:read"]}
      any={["performance-payouts:create"]}
    >
      <LazyPage>
        <StaffCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const staffEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/staff/$id/edit",
  component: () => (
    <RequirePermissions
      all={["performance-payouts:read"]}
      any={["performance-payouts:create"]}
    >
      <LazyPage>
        <StaffEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const outsourcesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/outsources",
  validateSearch: outsourcesSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-events:read"]}>
      <LazyPage>
        <OutsourcesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const outsourceCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/outsources/create",
  component: () => (
    <RequirePermissions
      all={["performance-events:read"]}
      any={["performance-events:create"]}
    >
      <LazyPage>
        <OutsourceCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const outsourceEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/outsources/$id/edit",
  component: () => (
    <RequirePermissions
      all={["performance-events:read"]}
      any={["performance-events:update"]}
    >
      <LazyPage>
        <OutsourceEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const outsourceCategoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/settings/outsource-categories",
  component: () => (
    <RequirePermissions all={["performance-events:read"]}>
      <LazyPage>
        <OutsourceCategoriesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const perDiemPoliciesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/per-diem",
  validateSearch: performanceSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-per-diem:read"]}>
      <LazyPage>
        <PerDiemPoliciesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleFeeConfigsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/role-fees",
  component: () => (
    <RequirePermissions all={["performance-role-fees:read"]}>
      <LazyPage>
        <RoleFeeConfigsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const perDiemPolicyCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/per-diem/create",
  component: () => (
    <RequirePermissions
      all={["performance-per-diem:read"]}
      any={["performance-per-diem:create"]}
    >
      <LazyPage>
        <PerDiemPolicyCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const perDiemPolicyEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/per-diem/$id/edit",
  component: () => (
    <RequirePermissions
      all={["performance-per-diem:read"]}
      any={["performance-per-diem:update"]}
    >
      <LazyPage>
        <PerDiemPolicyEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventCategoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/settings/event-categories",
  component: () => (
    <RequirePermissions all={["performance-events:read"]}>
      <LazyPage>
        <EventCategoriesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventCategoryCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/settings/event-categories/create",
  component: () => (
    <RequirePermissions
      all={["performance-events:read"]}
      any={["performance-events:update"]}
    >
      <LazyPage>
        <EventCategoryCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventCategoryEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/settings/event-categories/$id/edit",
  component: () => (
    <RequirePermissions
      all={["performance-events:read"]}
      any={["performance-events:update"]}
    >
      <LazyPage>
        <EventCategoryEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const paymentTermsTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/payment-terms-templates",
  validateSearch: performanceSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-payment-terms:read"]}>
      <LazyPage>
        <PaymentTermsTemplatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const paymentTermsTemplateCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/payment-terms-templates/create",
  component: () => (
    <RequirePermissions
      all={["performance-payment-terms:read"]}
      any={["performance-payment-terms:create"]}
    >
      <LazyPage>
        <PaymentTermsTemplateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const paymentTermsTemplateEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/payment-terms-templates/$id/edit",
  component: () => (
    <RequirePermissions
      all={["performance-payment-terms:read"]}
      any={["performance-payment-terms:update"]}
    >
      <LazyPage>
        <PaymentTermsTemplateEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const bankAccountTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/bank-account-templates",
  validateSearch: performanceSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-bank-account:read"]}>
      <LazyPage>
        <BankAccountTemplatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const bankAccountTemplateCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/bank-account-templates/create",
  component: () => (
    <RequirePermissions
      all={["performance-bank-account:read"]}
      any={["performance-bank-account:create"]}
    >
      <LazyPage>
        <BankAccountTemplateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const bankAccountTemplateEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/bank-account-templates/$id/edit",
  component: () => (
    <RequirePermissions
      all={["performance-bank-account:read"]}
      any={["performance-bank-account:update"]}
    >
      <LazyPage>
        <BankAccountTemplateEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const installmentPlanTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/installment-plan-templates",
  validateSearch: performanceSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-documents:read"]}>
      <LazyPage>
        <InstallmentPlanTemplatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const installmentPlanTemplateCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/installment-plan-templates/create",
  component: () => (
    <RequirePermissions
      all={["performance-documents:read"]}
      any={["performance-documents:create"]}
    >
      <LazyPage>
        <InstallmentPlanTemplateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const installmentPlanTemplateEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/installment-plan-templates/$id/edit",
  component: () => (
    <RequirePermissions
      all={["performance-documents:read"]}
      any={["performance-documents:create"]}
    >
      <LazyPage>
        <InstallmentPlanTemplateEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const outsourceApInstallmentPlanTemplatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/settings/outsource-ap-installment-plans",
  validateSearch: performanceSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-payouts:read"]}>
      <LazyPage>
        <OutsourceApInstallmentPlanTemplatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const outsourceApInstallmentPlanTemplateCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/settings/outsource-ap-installment-plans/create",
  component: () => (
    <RequirePermissions any={["performance-payouts:create"]}>
      <LazyPage>
        <OutsourceApInstallmentPlanTemplateCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const outsourceApInstallmentPlanTemplateEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/settings/outsource-ap-installment-plans/$id/edit",
  component: () => (
    <RequirePermissions any={["performance-payouts:create"]}>
      <LazyPage>
        <OutsourceApInstallmentPlanTemplateEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const taxSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/tax-settings",
  component: () => (
    <RequirePermissions all={["performance-documents:read"]}>
      <LazyPage>
        <TaxSettingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/events",
  validateSearch: eventsPageSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-events:read"]}>
      <LazyPage>
        <EventsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/events/create",
  validateSearch: z.object({
    requestId: z.string().optional(),
  }),
  component: () => (
    <RequirePermissions
      all={["performance-events:read"]}
      any={["performance-events:create"]}
    >
      <LazyPage>
        <EventCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventDetailSearchSchema = z.object({
  section: z
    .enum([
      "overview",
      "team",
      "staff",
      "pricing",
      "payout",
      "reports",
      "commission",
      "production_costs",
    ])
    .optional(),
});

const eventDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/events/$id",
  validateSearch: eventDetailSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-events:read"]}>
      <LazyPage>
        <EventDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/events/$id/edit",
  component: () => (
    <RequirePermissions
      all={["performance-events:read"]}
      any={["performance-events:update"]}
    >
      <LazyPage>
        <EventEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventRehearsalsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/events/$id/rehearsals",
  component: () => (
    <RequirePermissions all={["performance-events:read"]}>
      <LazyPage>
        <EventRehearsalPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const documentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/documents",
  component: () => (
    <RequirePermissions all={["performance-documents:read"]}>
      <LazyPage>
        <DocumentsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const payoutsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/payouts",
  component: () => (
    <RequirePermissions all={["performance-payouts:read"]}>
      <LazyPage>
        <PayoutsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const payoutDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/payouts/$id",
  component: () => (
    <RequirePermissions all={["performance-payouts:read"]}>
      <LazyPage>
        <PayoutDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performanceReportsDashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/reports",
  validateSearch: performanceReportSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-reports:read"]}>
      <LazyPage>
        <PerformanceReportsDashboardPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performanceEventReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/reports/events",
  validateSearch: performanceReportSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-reports:read"]}>
      <LazyPage>
        <PerformanceEventReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performancePayoutReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/reports/payouts",
  validateSearch: performanceReportSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-reports:read"]}>
      <LazyPage>
        <PerformancePayoutReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performanceRevenueReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/reports/revenue",
  validateSearch: performanceReportSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-reports:read"]}>
      <LazyPage>
        <PerformanceRevenueReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performanceRequestReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/reports/requests",
  validateSearch: performanceReportSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-reports:read"]}>
      <LazyPage>
        <PerformanceRequestReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const performancePerformerReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/performance/reports/performers",
  validateSearch: performanceReportSearchSchema,
  component: () => (
    <RequirePermissions all={["performance-reports:read"]}>
      <LazyPage>
        <PerformancePerformerReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute]),
  publicLayoutRoute.addChildren([
    publicBookingRoute,
    publicStudentProfileRoute,
  ]),
  publicFormShellRoute.addChildren([
    publicFormViewerResponsesRoute,
    publicFormViewerGateRoute,
    publicFormSuccessRoute,
    publicFormFillRoute,
  ]),
  studioLayoutRoute.addChildren([studioIndexRoute]),
  displayLayoutRoute.addChildren([displaySlugRoute]),
  appRoute.addChildren([
    dashboardRoute,
    formsListRoute,
    formsDashboardRoute,
    formsCategoriesRoute,
    formsNewRoute,
    formsEditRoute,
    formsResponsesRoute,
    rolesRoute,
    roleCreateRoute,
    roleEditRoute,
    usersRoute,
    userCreateRoute,
    userEditRoute,
    profileRoute,
    studentsRoute,
    studentProfileRoute,
    studentCreateRoute,
    studentEditRoute,
    classTypesRoute,
    classTypeCreateRoute,
    classTypeEditRoute,
    levelsRoute,
    levelCreateRoute,
    levelEditRoute,
    roomsRoute,
    roomCreateRoute,
    roomEditRoute,
    instructorsRoute,
    instructorCreateRoute,
    instructorProfileRoute,
    instructorEditRoute,
    studentGroupsRoute,
    studentGroupCreateRoute,
    studentGroupEditRoute,
    packageTemplatesRoute,
    packageTemplateCreateRoute,
    packageTemplateDetailRoute,
    packageTemplateEditRoute,
    sellRoute,
    partnerPendingLockRoute,
    partnerLockPricingRoute,
    sellSuccessRoute,
    privateLessonTemplatesRoute,
    privateLessonTemplateCreateRoute,
    privateLessonTemplateDetailRoute,
    privateLessonTemplateEditRoute,
    privateLessonPackagesRoute,
    privateLessonPackageDetailRoute,
    bulkSchedulePrivateLessonsRoute,
    privateLessonBookingsRoute,
    privateLessonBookingCreateRoute,
    privateLessonBookingBulkCreateRoute,
    privateLessonBookingDetailRoute,
    privateLessonBookingEditRoute,
    roomDisplaysRoute,
    roomDisplayCreateRoute,
    roomDisplayDetailRoute,
    roomBookingsRoute,
    roomBookingCreateRoute,
    roomBookingBulkCreateRoute,
    roomBookingSuccessRoute,
    roomBookingDetailRoute,
    roomBookingEditRoute,
    classTemplatesRoute,
    classTemplateCreateRoute,
    classTemplateDetailRoute,
    classTemplateEditRoute,
    classScheduleRoute,
    classSessionBulkCreateRoute,
    classSessionCreateRoute,
    classSessionsRoute,
    classSessionDetailRoute,
    classSessionEditRoute,
    scheduleChangeLogsRoute,
    scheduleChangeLogDetailRoute,
    attendanceCheckInRoute,
    attendanceRecordsRoute,
    receiptsRoute,
    receiptDetailRoute,
    creditInvoicesRoute,
    creditInvoiceDetailRoute,
    refundsRoute,
    refundDetailRoute,
    expensesOverviewRoute,
    expensesRoute,
    expenseCreateRoute,
    expenseCategoriesRoute,
    expenseEditRoute,
    expenseDetailRoute,
    campEventsRoute,
    campRegistrationsRoute,
    campWaitlistRoute,
    campRegistrationDetailRoute,
    classEnrollmentsRoute,
    classEnrollmentDetailRoute,
    jamEventsRoute,
    jamRegistrationsRoute,
    jamRegistrationDetailRoute,
    workshopRegistrationsRoute,
    visitorLotteryRoute,
    visitorLotteryRegistrationsRoute,
    visitorLotteryScannerRoute,
    visitorLotteryReportRoute,
    audienceVoteRoute,
    notificationsRoute,
    attendanceReportRoute,
    roomUsageReportRoute,
    instructorSessionReportRoute,
    instructorMonthlyPayoutReportRoute,
    revenueSalesReportRoute,
    packageSalesReportRoute,
    classTypeSalesReportRoute,
    refundReportRoute,
    classCapacityReportRoute,
    studentReportRoute,
    activeStudentsByClassReportRoute,
    packageNotificationsRoute,
    packageNotificationsSendRoute,
    packageNotificationsLogsRoute,
    packageNotificationsSettingsRoute,
    performanceIndexRoute,
    performanceOperationsDashboardRoute,
    requestsRoute,
    requestCreateRoute,
    requestDetailRoute,
    requestConvertRoute,
    requestEditRoute,
    performersRoute,
    performerCreateRoute,
    performerDetailRoute,
    performerEditRoute,
    staffRoute,
    staffCreateRoute,
    staffEditRoute,
    outsourcesRoute,
    outsourceCreateRoute,
    outsourceEditRoute,
    outsourceCategoriesRoute,
    roleFeeConfigsRoute,
    perDiemPoliciesRoute,
    perDiemPolicyCreateRoute,
    perDiemPolicyEditRoute,
    eventCategoriesRoute,
    eventCategoryCreateRoute,
    eventCategoryEditRoute,
    paymentTermsTemplatesRoute,
    paymentTermsTemplateCreateRoute,
    paymentTermsTemplateEditRoute,
    bankAccountTemplatesRoute,
    bankAccountTemplateCreateRoute,
    bankAccountTemplateEditRoute,
    installmentPlanTemplatesRoute,
    installmentPlanTemplateCreateRoute,
    installmentPlanTemplateEditRoute,
    outsourceApInstallmentPlanTemplatesRoute,
    outsourceApInstallmentPlanTemplateCreateRoute,
    outsourceApInstallmentPlanTemplateEditRoute,
    taxSettingsRoute,
    eventsRoute,
    eventCreateRoute,
    eventDetailRoute,
    eventEditRoute,
    eventRehearsalsRoute,
    documentsRoute,
    payoutsRoute,
    payoutDetailRoute,
    performanceReportsDashboardRoute,
    performanceEventReportRoute,
    performancePayoutReportRoute,
    performanceRevenueReportRoute,
    performanceRequestReportRoute,
    performancePerformerReportRoute,
  ]),
  forbiddenRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
